import { auth } from './auth.svelte.js';
import { getLastUsername } from '../sogni-client.svelte.js';
import { normalizePersistedImageModelIds, MATURE_IMAGE_MODEL_IDS } from '../utils/image-model-catalog.js';
import { imageModels, modelCatalog } from './model-catalog.svelte.js';
import { stopSpeaking } from '../utils/tts-audio.js';
import { coverResetNeeded } from '../utils/mature-content.js';

const STORAGE_KEY_PREFIX = 'studio-settings';
const SETTINGS_VERSION = '1.5'; // Bumped for per-user localStorage
const MODEL_DEFAULTS_VERSION = '2026-06-krea-z-default';
// Bump to backfill the Default Style onto existing users who never set one.
const STYLE_DEFAULTS_VERSION = '2026-07-default-style';
// The default appended to every prompt (matches app.sogni.ai's Default style).
const DEFAULT_STYLE = 'intricate details, breathtaking, amazing, award-winning';
const Z_IMAGE_TURBO_MODEL_ID = 'z_image_turbo_bf16';
const KREA_2_TURBO_MODEL_ID = 'krea2_turbo_fp8_scaled';
export const DEFAULT_SELECTED_MODELS = [KREA_2_TURBO_MODEL_ID, Z_IMAGE_TURBO_MODEL_ID];

// Models that require Sogni's Mature Content Filter to be disabled to render.
// These are NSFW community fine-tunes; they only work when `matureContentEnabled`
// is on (which is login-gated + 18+ confirmed). See modelRequiresMatureContent().
export const MATURE_CONTENT_MODEL_IDS = [...MATURE_IMAGE_MODEL_IDS];

export function modelRequiresMatureContent(modelId) {
  return MATURE_CONTENT_MODEL_IDS.includes(modelId);
}

// TTS (Text-to-Speech) settings - separate storage for global voice toggle
const TTS_STORAGE_KEY_PREFIX = 'tts-settings';
const TTS_SETTINGS_VERSION = '2.0';

// Get user-specific storage key
function getStorageKey(username) {
  if (!username) return null;
  return `${STORAGE_KEY_PREFIX}-${username}`;
}

// Get user-specific TTS storage key
function getTTSStorageKey(username) {
  if (!username) return null;
  return `${TTS_STORAGE_KEY_PREFIX}-${username}`;
}

function loadTTSSettingsForUser(username) {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return { enabled: false }; // Default to OFF
  }

  const storageKey = getTTSStorageKey(username);
  if (!storageKey) {
    return { enabled: false }; // Default to OFF - not logged in
  }

  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return { enabled: false }; // Default to OFF
    }
    const parsed = JSON.parse(stored);
    if (parsed?.version !== TTS_SETTINGS_VERSION) {
      const resetSettings = { enabled: false, version: TTS_SETTINGS_VERSION };
      localStorage.setItem(storageKey, JSON.stringify(resetSettings));
      return { enabled: false };
    }
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : false
    };
  } catch (error) {
    console.warn('Failed to parse TTS settings:', error);
    return { enabled: false };
  }
}

function getInitialTTSEnabled() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }

  const lastUsername = getLastUsername();
  if (!lastUsername) {
    return false;
  }

  return loadTTSSettingsForUser(lastUsername).enabled;
}

function persistTTSSettings(settings) {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

  // Only persist if user is logged in
  if (!auth.isLoggedIn || !auth.username) {
    return;
  }

  const storageKey = getTTSStorageKey(auth.username);
  if (!storageKey) {
    return;
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify({
      ...settings,
      version: TTS_SETTINGS_VERSION
    }));
  } catch (error) {
    console.warn('Failed to persist TTS settings:', error);
  }
}

// Global TTS settings state - initialized with defaults, loaded when user logs in
export const ttsSettings = $state({ enabled: getInitialTTSEnabled() });

// Global TTS playback state - tracks whether audio is currently playing
export const ttsPlayback = $state({ isSpeaking: false, error: null });

export function setTTSSpeaking(speaking) {
  ttsPlayback.isSpeaking = speaking;
}

export function toggleTTS() {
  ttsSettings.enabled = !ttsSettings.enabled;
  // Stop all playing audio when disabling TTS
  if (!ttsSettings.enabled) {
    stopSpeaking();
  }
  persistTTSSettings({ enabled: ttsSettings.enabled });
}

export function setTTSEnabled(enabled) {
  ttsSettings.enabled = enabled;
  persistTTSSettings({ enabled: ttsSettings.enabled });
}

// Aspect ratio presets with dimensions
// Ordered from tallest to widest for intuitive UI
export const ASPECT_RATIOS = {
  '9:16': { label: '9:16', standard: { w: 512, h: 896 }, hd: { w: 1024, h: 1792 } },
  '2:3':  { label: '2:3',  standard: { w: 512, h: 768 }, hd: { w: 1024, h: 1536 } },
  '3:4':  { label: '3:4',  standard: { w: 576, h: 768 }, hd: { w: 1024, h: 1360 } },
  '1:1':  { label: '1:1',  standard: { w: 512, h: 512 }, hd: { w: 1024, h: 1024 } },
  '4:3':  { label: '4:3',  standard: { w: 768, h: 576 }, hd: { w: 1360, h: 1024 } },
  '3:2':  { label: '3:2',  standard: { w: 768, h: 512 }, hd: { w: 1536, h: 1024 } },
  '16:9': { label: '16:9', standard: { w: 896, h: 512 }, hd: { w: 1792, h: 1024 } },
  '5:2':  { label: '5:2',  standard: { w: 1280, h: 512 }, hd: { w: 1920, h: 768 } }
};

export const ASPECT_RATIO_ORDER = ['9:16', '2:3', '3:4', '1:1', '4:3', '3:2', '16:9', '5:2'];

// Video aspect ratio presets with smaller dimensions suitable for video generation
// Videos use smaller base sizes for faster generation
export const VIDEO_ASPECT_RATIOS = {
  '9:16': { label: '9:16', small: { w: 480, h: 848 }, medium: { w: 640, h: 1136 } },
  '2:3':  { label: '2:3',  small: { w: 480, h: 720 }, medium: { w: 640, h: 960 } },
  '3:4':  { label: '3:4',  small: { w: 480, h: 640 }, medium: { w: 640, h: 848 } },
  '1:1':  { label: '1:1',  small: { w: 480, h: 480 }, medium: { w: 640, h: 640 } },
  '4:3':  { label: '4:3',  small: { w: 640, h: 480 }, medium: { w: 848, h: 640 } },
  '3:2':  { label: '3:2',  small: { w: 720, h: 480 }, medium: { w: 960, h: 640 } },
  '16:9': { label: '16:9', small: { w: 848, h: 480 }, medium: { w: 1136, h: 640 } },
  '5:2':  { label: '5:2',  small: { w: 800, h: 320 }, medium: { w: 1200, h: 480 } }
};

// Get video dimensions based on aspect ratio and resolution
// aspectRatio: one of the keys in VIDEO_ASPECT_RATIOS
// resolution: 'small' (~480p base) or 'medium' (~640p base)
export function getVideoDimensions(aspectRatio = '1:1', resolution = 'small') {
  const ratio = VIDEO_ASPECT_RATIOS[aspectRatio] || VIDEO_ASPECT_RATIOS['1:1'];
  const dims = resolution === 'medium' ? ratio.medium : ratio.small;
  return { width: dims.w, height: dims.h };
}

// Public live catalog; the stable reactive array also provides gallery labels.
export { imageModels as AVAILABLE_MODELS };

// Batches per model is now always 1 (feature removed from UI)
function getDefaultBatchesPerModel() {
  return 1;
}

const defaultSettings = {
  batchRenders: 2, // Default to 2 images per batch for first-time users
  batchesPerModel: 1, // Default to 1 batch per model for first-time users
  promptStylesAlike: DEFAULT_STYLE,
  selectedModels: DEFAULT_SELECTED_MODELS, // Default to Krea 2 Turbo + Z-Image Turbo
  network: 'fast',
  favoriteModelIds: [],
  modelOverrides: {},
  renderPreset: 'final',
  // Mature content (18+) — disables Sogni's Mature Content Filter so the 🔞 models
  // can render. Off by default; requires login + an 18+ confirmation to enable.
  matureContentEnabled: false,
  matureContentConfirmed: false,
  // Image size settings
  aspectRatio: '1:1',      // Default to square
  resolution: 'hd',        // 'standard' (512 base) or 'hd' (1024 base)
  useCustomSize: false,
  customWidth: 1024,
  customHeight: 1024
};

function cloneDefaults() {
  return {
    batchRenders: defaultSettings.batchRenders,
    batchesPerModel: defaultSettings.batchesPerModel,
    promptStylesAlike: defaultSettings.promptStylesAlike,
    selectedModels: [...defaultSettings.selectedModels],
    network: defaultSettings.network,
    favoriteModelIds: [],
    modelOverrides: {},
    renderPreset: defaultSettings.renderPreset,
    matureContentEnabled: defaultSettings.matureContentEnabled,
    matureContentConfirmed: defaultSettings.matureContentConfirmed,
    aspectRatio: defaultSettings.aspectRatio,
    resolution: defaultSettings.resolution,
    useCustomSize: defaultSettings.useCustomSize,
    customWidth: defaultSettings.customWidth,
    customHeight: defaultSettings.customHeight
  };
}

// Viewing preferences change what the viewer sees, never a request: they stay
// out of studioSettings so toggling one cannot void an in-flight approval
// (generationSourceFingerprint freezes studioSettings), and out of the saved
// session snapshot. They persist in the same per-user blob as the settings.
const DEFAULT_BLUR_MATURE_CONTENT = true; // matches app.sogni.ai's "Blur mature content"

function defaultViewingPreferences() {
  return { blurMatureContent: DEFAULT_BLUR_MATURE_CONTENT };
}

function clampBatchCount(value) {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return defaultSettings.batchRenders;
  }
  return Math.min(16, Math.max(1, Math.round(parsed)));
}

function clampBatchesPerModel() {
  return 1; // Feature removed - always 1
}

function normalizeSelectedModels(selectedModels) {
  // A missing worker or another network must not replace the user's selection.
  const supportedIds = modelCatalog.status === 'ready'
    ? new Set(imageModels.map((model) => model.id)) : null;
  return normalizePersistedImageModelIds(selectedModels, defaultSettings.selectedModels, supportedIds);
}

function buildPersistedSettings(settings, viewing) {
  return {
    version: SETTINGS_VERSION,
    modelDefaultsVersion: MODEL_DEFAULTS_VERSION,
    styleDefaultsVersion: STYLE_DEFAULTS_VERSION,
    batchRenders: settings.batchRenders,
    batchesPerModel: settings.batchesPerModel,
    promptStylesAlike: settings.promptStylesAlike,
    selectedModels: settings.selectedModels,
    network: settings.network,
    favoriteModelIds: settings.favoriteModelIds,
    modelOverrides: settings.modelOverrides,
    renderPreset: settings.renderPreset,
    matureContentEnabled: settings.matureContentEnabled,
    matureContentConfirmed: settings.matureContentConfirmed,
    blurMatureContent: viewing.blurMatureContent,
    // Image size settings
    aspectRatio: settings.aspectRatio,
    resolution: settings.resolution,
    useCustomSize: settings.useCustomSize,
    customWidth: settings.customWidth,
    customHeight: settings.customHeight
  };
}

function clampDimension(value) {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return 512;
  }
  // UI envelope; each model validates its exact range/grid before submission.
  return Math.round(Math.min(3840, Math.max(256, parsed)) / 8) * 8;
}

function parseVersion(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const cleaned = value.trim().replace(/^v/i, '');
  if (!cleaned) {
    return null;
  }

  const parts = cleaned.split('.');
  const numbers = parts.map(part => Number.parseInt(part, 10));

  if (numbers.some(num => Number.isNaN(num))) {
    return null;
  }

  return {
    major: numbers[0] ?? 0,
    minor: numbers[1] ?? 0,
    patch: numbers[2] ?? 0
  };
}

function isVersionAtLeast(version, minimum) {
  const parsedVersion = parseVersion(version);
  const parsedMinimum = parseVersion(minimum);

  if (!parsedVersion || !parsedMinimum) {
    return false;
  }

  if (parsedVersion.major !== parsedMinimum.major) {
    return parsedVersion.major > parsedMinimum.major;
  }

  if (parsedVersion.minor !== parsedMinimum.minor) {
    return parsedVersion.minor > parsedMinimum.minor;
  }

  return parsedVersion.patch >= parsedMinimum.patch;
}

function loadStoredSettingsForUser(username) {
  const defaults = { settings: cloneDefaults(), viewing: defaultViewingPreferences() };
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return defaults;
  }

  const storageKey = getStorageKey(username);
  if (!storageKey) {
    return defaults; // Not logged in - use defaults
  }

  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return defaults;
    }

    const parsed = JSON.parse(stored);

    const storedVersion = typeof parsed.version === 'string' ? parsed.version : null;
    if (!isVersionAtLeast(storedVersion, SETTINGS_VERSION)) {
      localStorage.removeItem(storageKey);
      return defaults;
    }

    const shouldApplyModelDefault = parsed.modelDefaultsVersion !== MODEL_DEFAULTS_VERSION;
    const storedSelectedModels = Array.isArray(parsed.selectedModels) ? parsed.selectedModels : [];
    const normalizedStoredModels = normalizeSelectedModels(storedSelectedModels);
    const shouldMigrateSelectedModels = !shouldApplyModelDefault && (
      storedSelectedModels.length !== normalizedStoredModels.length
      || storedSelectedModels.some((modelId, index) => modelId !== normalizedStoredModels[index])
    );
    // One-time backfill of the Default Style for users who never set one (empty).
    // Users with a custom style keep it; once backfilled, clearing the field sticks.
    const shouldBackfillStyle = parsed.styleDefaultsVersion !== STYLE_DEFAULTS_VERSION;
    const storedStyle = typeof parsed.promptStylesAlike === 'string'
      ? parsed.promptStylesAlike
      : defaultSettings.promptStylesAlike;
    const loadedSettings = {
      batchRenders: clampBatchCount(parsed.batchRenders ?? defaultSettings.batchRenders),
      batchesPerModel: clampBatchesPerModel(parsed.batchesPerModel ?? defaultSettings.batchesPerModel),
      promptStylesAlike: shouldBackfillStyle && storedStyle.trim() === ''
        ? DEFAULT_STYLE
        : storedStyle,
      selectedModels: shouldApplyModelDefault
        ? [...DEFAULT_SELECTED_MODELS]
        : normalizedStoredModels,
      network: parsed.network === 'relaxed' ? 'relaxed' : 'fast',
      favoriteModelIds: Array.isArray(parsed.favoriteModelIds)
        ? [...new Set(parsed.favoriteModelIds.filter((id) => typeof id === 'string' && id))] : [],
      modelOverrides: parsed.modelOverrides && typeof parsed.modelOverrides === 'object' && !Array.isArray(parsed.modelOverrides)
        ? parsed.modelOverrides : {},
      renderPreset: parsed.renderPreset === 'draft' ? 'draft' : 'final',
      matureContentEnabled: typeof parsed.matureContentEnabled === 'boolean' ? parsed.matureContentEnabled : false,
      matureContentConfirmed: typeof parsed.matureContentConfirmed === 'boolean' ? parsed.matureContentConfirmed : false,
      // Image size settings
      aspectRatio: ASPECT_RATIOS[parsed.aspectRatio] ? parsed.aspectRatio : defaultSettings.aspectRatio,
      resolution: ['standard', 'hd'].includes(parsed.resolution) ? parsed.resolution : defaultSettings.resolution,
      useCustomSize: typeof parsed.useCustomSize === 'boolean' ? parsed.useCustomSize : defaultSettings.useCustomSize,
      customWidth: clampDimension(parsed.customWidth),
      customHeight: clampDimension(parsed.customHeight)
    };

    const viewing = { blurMatureContent: typeof parsed.blurMatureContent === 'boolean'
      ? parsed.blurMatureContent : DEFAULT_BLUR_MATURE_CONTENT };

    if (shouldApplyModelDefault || shouldBackfillStyle || shouldMigrateSelectedModels) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(buildPersistedSettings(loadedSettings, viewing)));
      } catch (migrationError) {
        console.warn('Failed to persist migrated studio settings:', migrationError);
      }
    }

    return { settings: loadedSettings, viewing };
  } catch (error) {
    console.warn('Failed to parse stored studio settings:', error);
    return defaults;
  }
}

// Initialize with defaults - settings will be loaded when user logs in
export const studioSettings = $state(cloneDefaults());

// Debug: Log the initial loaded settings
console.log('[Settings Module] Initial studioSettings (defaults):', {
  batchRenders: studioSettings.batchRenders,
  selectedModels: studioSettings.selectedModels
});

// Keep results the server labelled sensitive covered until tapped, even while
// the filter is off. See the note above defaultViewingPreferences().
export const viewingPreferences = $state(defaultViewingPreferences());

// Page-local reveals of labelled results, one key per result (see
// state/sensitive-reveals.svelte.js, which reads and writes the keys). Owned
// here so the filter or the blur coming back on, a settings reset and an
// account change clear every reveal without that module being told.
export const sensitiveReveals = $state({ keys: {} });

export function clearSensitiveReveals() {
  sensitiveReveals.keys = {};
}

function coverSwitches() {
  return { matureContentEnabled: studioSettings.matureContentEnabled, blurMatureContent: viewingPreferences.blurMatureContent };
}

export const studioSettingsUI = $state({
  isOpen: false,
  pauseInitialGeneration: false,
  autoOpenedForSession: false,
  renderSetupOpen: false
});

function persistSettings() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return;
  }

  // Only persist if user is logged in
  if (!auth.isLoggedIn || !auth.username) {
    return;
  }

  const storageKey = getStorageKey(auth.username);
  if (!storageKey) {
    return;
  }

  try {
    localStorage.setItem(storageKey, JSON.stringify(buildPersistedSettings({
      batchRenders: studioSettings.batchRenders,
      batchesPerModel: studioSettings.batchesPerModel,
      promptStylesAlike: studioSettings.promptStylesAlike,
      selectedModels: studioSettings.selectedModels,
      network: studioSettings.network,
      favoriteModelIds: studioSettings.favoriteModelIds,
      modelOverrides: studioSettings.modelOverrides,
      renderPreset: studioSettings.renderPreset,
      matureContentEnabled: studioSettings.matureContentEnabled,
      matureContentConfirmed: studioSettings.matureContentConfirmed,
      // Image size settings
      aspectRatio: studioSettings.aspectRatio,
      resolution: studioSettings.resolution,
      useCustomSize: studioSettings.useCustomSize,
      customWidth: studioSettings.customWidth,
      customHeight: studioSettings.customHeight
    }, viewingPreferences)));
  } catch (error) {
    console.warn('Failed to persist studio settings:', error);
  }
}

export function applyStudioSettings(partial = {}) {
  if (partial.batchRenders !== undefined) {
    studioSettings.batchRenders = clampBatchCount(partial.batchRenders);
  }

  if (partial.batchesPerModel !== undefined) {
    studioSettings.batchesPerModel = clampBatchesPerModel(partial.batchesPerModel);
  }

  if (partial.promptStylesAlike !== undefined) {
    studioSettings.promptStylesAlike = String(partial.promptStylesAlike || '');
  }

  // Mature content is login-gated: guests can never enable it.
  const coverBefore = coverSwitches();
  if (partial.matureContentEnabled !== undefined) {
    studioSettings.matureContentEnabled = auth.isLoggedIn && Boolean(partial.matureContentEnabled);
  }
  if (partial.matureContentConfirmed !== undefined) {
    studioSettings.matureContentConfirmed = Boolean(partial.matureContentConfirmed);
  }
  if (coverResetNeeded(coverBefore, coverSwitches())) clearSensitiveReveals();

  if (partial.selectedModels !== undefined) {
    studioSettings.selectedModels = normalizeSelectedModels(partial.selectedModels);
  }
  if (['fast', 'relaxed'].includes(partial.network)) studioSettings.network = partial.network;
  if (partial.modelOverrides !== undefined) studioSettings.modelOverrides = { ...partial.modelOverrides };
  if (['draft', 'final'].includes(partial.renderPreset)) studioSettings.renderPreset = partial.renderPreset;

  // Safety net: when mature content is off, no 🔞 model may stay selected
  // (normalizeSelectedModels falls back to defaults if this empties the list).
  if (!studioSettings.matureContentEnabled) {
    studioSettings.selectedModels = normalizeSelectedModels(
      studioSettings.selectedModels.filter((id) => !modelRequiresMatureContent(id))
    );
  }

  // Image size settings
  if (partial.aspectRatio !== undefined && ASPECT_RATIOS[partial.aspectRatio]) {
    studioSettings.aspectRatio = partial.aspectRatio;
  }

  if (partial.resolution !== undefined && ['standard', 'hd'].includes(partial.resolution)) {
    studioSettings.resolution = partial.resolution;
  }

  if (partial.useCustomSize !== undefined) {
    studioSettings.useCustomSize = Boolean(partial.useCustomSize);
  }

  if (partial.customWidth !== undefined) {
    studioSettings.customWidth = clampDimension(partial.customWidth);
  }

  if (partial.customHeight !== undefined) {
    studioSettings.customHeight = clampDimension(partial.customHeight);
  }

  studioSettingsUI.pauseInitialGeneration = false;
  studioSettingsUI.isOpen = false;
  persistSettings();
}

export function toggleFavoriteModel(modelId) {
  if (!auth.isLoggedIn || !modelId) return;
  studioSettings.favoriteModelIds = studioSettings.favoriteModelIds.includes(modelId)
    ? studioSettings.favoriteModelIds.filter((id) => id !== modelId)
    : [...studioSettings.favoriteModelIds, modelId];
  persistSettings();
}

/**
 * Persist the Mature Content preference immediately (independent of the settings
 * panel's draft/Save flow). Enabling it is a consent gate that must stick across
 * refreshes even if the user never clicks "Save"; disabling it also drops any
 * persisted 🔞 models. Does NOT close the panel (unlike applyStudioSettings).
 */
export function setMatureContentPreference(enabled) {
  const on = auth.isLoggedIn && Boolean(enabled);
  const coverBefore = coverSwitches();
  studioSettings.matureContentEnabled = on;
  if (on) {
    studioSettings.matureContentConfirmed = true;
  } else {
    studioSettings.selectedModels = normalizeSelectedModels(
      studioSettings.selectedModels.filter((id) => !modelRequiresMatureContent(id))
    );
  }
  // The filter coming back on re-covers every revealed result.
  if (coverResetNeeded(coverBefore, coverSwitches())) clearSensitiveReveals();
  persistSettings();
}

/**
 * Persist the "Blur mature content" viewing preference immediately. It changes
 * no request, so it never touches studioSettings or an in-flight approval.
 */
export function setBlurMatureContentPreference(enabled) {
  const coverBefore = coverSwitches();
  viewingPreferences.blurMatureContent = Boolean(enabled);
  // The blur coming back on re-covers every revealed result.
  if (coverResetNeeded(coverBefore, coverSwitches())) clearSensitiveReveals();
  persistSettings();
}

export function resetStudioSettings() {
  const defaults = cloneDefaults();
  studioSettings.batchRenders = defaults.batchRenders;
  studioSettings.batchesPerModel = defaults.batchesPerModel;
  studioSettings.promptStylesAlike = defaults.promptStylesAlike;
  studioSettings.selectedModels = defaults.selectedModels;
  studioSettings.network = defaults.network;
  studioSettings.favoriteModelIds = [];
  studioSettings.modelOverrides = {};
  studioSettings.renderPreset = defaults.renderPreset;
  studioSettings.matureContentEnabled = defaults.matureContentEnabled;
  studioSettings.matureContentConfirmed = defaults.matureContentConfirmed;
  viewingPreferences.blurMatureContent = DEFAULT_BLUR_MATURE_CONTENT;
  clearSensitiveReveals();
  studioSettings.aspectRatio = defaults.aspectRatio;
  studioSettings.resolution = defaults.resolution;
  studioSettings.useCustomSize = defaults.useCustomSize;
  studioSettings.customWidth = defaults.customWidth;
  studioSettings.customHeight = defaults.customHeight;
  persistSettings();
}

export function getDefaultStudioSettings() {
  return cloneDefaults();
}

export function openStudioSettings({ auto = false } = {}) {
  studioSettingsUI.isOpen = true;
  if (auto) {
    studioSettingsUI.autoOpenedForSession = true;
  }
}

export function closeStudioSettings({ resume = true } = {}) {
  studioSettingsUI.isOpen = false;
  if (resume) {
    studioSettingsUI.pauseInitialGeneration = false;
  }
}

export function toggleStudioSettings() {
  if (studioSettingsUI.isOpen) {
    closeStudioSettings();
  } else {
    openStudioSettings();
  }
}

export function resetStudioSettingsUI() {
  studioSettingsUI.isOpen = false;
  studioSettingsUI.pauseInitialGeneration = false;
  studioSettingsUI.autoOpenedForSession = false;
  studioSettingsUI.renderSetupOpen = false;
}

// Pre-render setup modal (models + image size before the first batch of a session)
export function openRenderSetup() {
  studioSettingsUI.renderSetupOpen = true;
}

export function closeRenderSetup() {
  studioSettingsUI.renderSetupOpen = false;
}

// Get the effective images per batch. Rendering requires sign-in, so there
// are no guest caps.
export function getEffectiveImagesPerBatch() {
  return studioSettings.batchRenders;
}

// Get the variant count (batches per model) - always returns 1 (feature removed)
export function getVariantCount() {
  return 1;
}

// Get the current image dimensions based on settings
export function getImageDimensions() {
  if (studioSettings.useCustomSize) {
    return {
      width: studioSettings.customWidth,
      height: studioSettings.customHeight
    };
  }

  const ratio = ASPECT_RATIOS[studioSettings.aspectRatio] || ASPECT_RATIOS['1:1'];
  const dims = studioSettings.resolution === 'hd' ? ratio.hd : ratio.standard;

  return {
    width: dims.w,
    height: dims.h
  };
}

// Get the selected models
export function getEffectiveModels() {
  return studioSettings.selectedModels;
}

// Get the maximum number of prompts/variants for generation (no cap)
export function getEffectiveMaxPrompts() {
  return Infinity;
}

// Load user-specific settings when user logs in
// Called from auth.svelte.js after successful login
export function loadUserSettings(username) {
  if (!username) {
    console.warn('[Settings] Cannot load user settings - no username provided');
    return;
  }

  console.log('[Settings] Loading settings for user:', username);

  // Load studio settings
  const { settings: loadedStudioSettings, viewing } = loadStoredSettingsForUser(username);
  studioSettings.batchRenders = loadedStudioSettings.batchRenders;
  studioSettings.batchesPerModel = loadedStudioSettings.batchesPerModel;
  studioSettings.promptStylesAlike = loadedStudioSettings.promptStylesAlike;
  studioSettings.selectedModels = loadedStudioSettings.selectedModels;
  studioSettings.network = loadedStudioSettings.network;
  studioSettings.favoriteModelIds = loadedStudioSettings.favoriteModelIds;
  studioSettings.modelOverrides = loadedStudioSettings.modelOverrides;
  studioSettings.renderPreset = loadedStudioSettings.renderPreset;
  studioSettings.matureContentEnabled = loadedStudioSettings.matureContentEnabled;
  studioSettings.matureContentConfirmed = loadedStudioSettings.matureContentConfirmed;
  viewingPreferences.blurMatureContent = viewing.blurMatureContent;
  clearSensitiveReveals(); // a different account's reveals never carry over
  studioSettings.aspectRatio = loadedStudioSettings.aspectRatio;
  studioSettings.resolution = loadedStudioSettings.resolution;
  studioSettings.useCustomSize = loadedStudioSettings.useCustomSize;
  studioSettings.customWidth = loadedStudioSettings.customWidth;
  studioSettings.customHeight = loadedStudioSettings.customHeight;

  // Load TTS settings
  const loadedTTSSettings = loadTTSSettingsForUser(username);
  ttsSettings.enabled = loadedTTSSettings.enabled;

  console.log('[Settings] Loaded user settings:', {
    batchRenders: studioSettings.batchRenders,
    selectedModels: studioSettings.selectedModels,
    ttsEnabled: ttsSettings.enabled
  });
}

// Reset settings to defaults when user logs out
// Called from auth.svelte.js on logout
export function clearUserSettings() {
  console.log('[Settings] Clearing user settings - resetting to defaults');

  // Reset studio settings to defaults
  const defaults = cloneDefaults();
  studioSettings.batchRenders = defaults.batchRenders;
  studioSettings.batchesPerModel = defaults.batchesPerModel;
  studioSettings.promptStylesAlike = defaults.promptStylesAlike;
  studioSettings.selectedModels = [...defaults.selectedModels];
  studioSettings.network = defaults.network;
  studioSettings.favoriteModelIds = [];
  studioSettings.modelOverrides = {};
  studioSettings.renderPreset = defaults.renderPreset;
  studioSettings.matureContentEnabled = defaults.matureContentEnabled;
  studioSettings.matureContentConfirmed = defaults.matureContentConfirmed;
  viewingPreferences.blurMatureContent = DEFAULT_BLUR_MATURE_CONTENT;
  clearSensitiveReveals();
  studioSettings.aspectRatio = defaults.aspectRatio;
  studioSettings.resolution = defaults.resolution;
  studioSettings.useCustomSize = defaults.useCustomSize;
  studioSettings.customWidth = defaults.customWidth;
  studioSettings.customHeight = defaults.customHeight;

  // Reset TTS settings to defaults
  ttsSettings.enabled = false;
}

// Export helper for components
export { getDefaultBatchesPerModel, clampDimension };
